import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectUser, selectUserPagesLoading, setLastAggregatePageVisited } from '../redux/systemSlice'
import { DOC_TEMPLATES } from '../constants'
import UserDocs, { ExistingDoc } from '../Components/UserDocs/UserDocs'
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import { useHistory } from 'react-router-dom'
import { LuBolt, LuBrain, LuClock, LuFile, LuFileSearch, LuScanText, LuZap } from 'react-icons/lu'
import { TbPencilMinus } from 'react-icons/tb'
import { FaListOl, FaRegFile } from 'react-icons/fa6'
import { RiRobot2Line } from 'react-icons/ri'
import Button from '../Components/Buttons'
import { BiChevronRight } from 'react-icons/bi'
import UpgradeModals from '../Components/Modals/Upgrade'
import TrialUpgrade from '../Components/Modals/TrialUpgrade'
import useIsPremium from '../hooks/useIsPremium'
import useTrialEssayCredits from '../hooks/useTrialEssayCredits'
import { FaPlus } from 'react-icons/fa'
import { Skeleton } from '@mui/material'
import { useAuth } from '../contexts/Auth'
import { FEATURE_FLAGS } from '../hooks/featureFlags'

export type templateType = {
	id: string
	title: string
	icon?: ReactNode | null
	desc: string
	label?: string
	href?: string
}

export const useOptions = (): templateType[] => {
	const idToDesc = {
		[DOC_TEMPLATES.Writer]: 'Choose a topic, word count, tone, reading level, and more.',
		[DOC_TEMPLATES.Outline]: "Choose a topic and we'll generate an outline for you to complete.",
		[DOC_TEMPLATES.Blank]: 'Start yourself and use our autocomplete tool to guide you.',
		humanizer: 'Paste in text and we will rewrite it to avoid AI detection.',
		detector: 'Paste in text and we will tell you if it was written by AI.',
	}

	return [
		{
			id: DOC_TEMPLATES.Writer,
			title: 'AI Essay Writer',
			icon: TbPencilMinus({ size: 32, className: 'mb-2 block' }),
			desc: idToDesc[DOC_TEMPLATES.Writer],
		},
		{
			id: 'humanizer',
			title: 'AI Humanizer',
			icon: RiRobot2Line({ size: 32, className: 'mb-2 block' }),
			desc: idToDesc['humanizer'],
			href: '/humanizer',
		},
		{
			id: 'detector',
			title: 'AI Detector',
			icon: LuFileSearch({ size: 32, className: 'mb-2 block' }),
			desc: idToDesc['detector'],
			href: '/detector',
		},
		{
			id: DOC_TEMPLATES.Outline,
			title: 'AI Essay Outliner',
			icon: FaListOl({ size: 26, className: 'mb-1 block' }),
			desc: idToDesc[DOC_TEMPLATES.Outline],
		},
		{
			id: DOC_TEMPLATES.Blank,
			title: 'Blank Document',
			icon: FaRegFile({ size: 32, className: 'mb-2 block' }),
			desc: idToDesc[DOC_TEMPLATES.Blank],
		},
	]
}

const pageName = 'doc'

function Doc() {
	const [userDocs, setUserDocs] = useState<ExistingDoc[]>([])
	const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false)
	const [loginModalOpen, setLoginModalOpen] = useState(false)
	const [trialUpgradeModalOpen, setTrialUpgradeModalOpen] = useState(false)
	const isPremium = useIsPremium()
	const { essaysRemaining, essayLimit } = useTrialEssayCredits()
	const dispatch = useDispatch()
	const posthog = usePostHog()
	const history = useHistory()
	const userPagesLoading = useSelector(selectUserPagesLoading)
	const { currentUser } = useAuth() as any
	const user = useSelector(selectUser)

	useEffect(() => {
		dispatch(setLastAggregatePageVisited({ value: '/choose' }))
	}, [dispatch])

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [])

	const accountModals = (
		<>
			<UpgradeModals
				subscriptionModalOpen={subscriptionModalOpen}
				loginModalOpen={loginModalOpen}
				setSubscriptionModalOpen={(open: boolean) => {
					if (!currentUser) {
						setLoginModalOpen(open)
					}
					setSubscriptionModalOpen(open)
				}}
				setLoginModalOpen={(open: boolean) => {
					setLoginModalOpen(open)
				}}
				premiumPath={true}
				context="dashboard"
			/>
			<TrialUpgrade
				open={trialUpgradeModalOpen}
				setModalOpen={setTrialUpgradeModalOpen}
				openForPaymentRetries={true}
				outOfCredits={true}
				openForUndetectable={false}
			/>
		</>
	)

	const greeting = useMemo(() => {
		const now = new Date()
		const currentHour = now.getHours()
		if (currentHour < 12) {
			return 'Good morning'
		} else if (currentHour < 18) {
			return 'Good afternoon'
		} else {
			return 'Good evening'
		}
	}, [])

	const ButtonWithArrow = ({ children, ...props }: any) => {
		return (
			<Button {...props} className="flex items-center group hover:pr-5 pr-0 transition-all z-10">
				{children}
				<span className="text-2xl h-4 w-4 group-hover:visible invisible group-hover:opacity-100 relative right-2 bottom-1 group-hover:-right-1 opacity-0 transition-all">
					{BiChevronRight({})}
				</span>
			</Button>
		)
	}

	return (
		<div className="bg-gray-50 min-h-screen">
			{accountModals}
			<main id={pageName} className="px-2 pt-24 pb-36 max-w-[1100px] mx-auto bg-gray-50">
				<div className="flex gap-4 justify-between items-center flex-wrap-reverse">
					<h5 className="md:text-3xl text-center text-2xl font-semibold">{greeting}, how can I help you write?</h5>
					{user.isOnFreeTrial ? (
						<div
							className={`flex gap-2 items-center justify-center md:justify-end w-full md:w-auto ${
								currentUser ? '' : 'md:flex hidden'
							}`}
							onClick={() => {
								if (!currentUser) {
									setLoginModalOpen(true)
								}
								posthog?.capture('nav-upgrade-button-clicked')
							}}
						>
							<div className="bg-gray-200 px-3 rounded-xl flex items-center text-sm py-1 gap-1.5">
								<span>Limited Trial</span>
								<span className="font-medium">·</span>
								<span className={`font-semibold ${essaysRemaining <= 1 ? 'text-red-600' : ''}`}>
									{essaysRemaining} essay{essaysRemaining !== 1 ? 's' : ''} left
								</span>
							</div>
							<Button
								type="default"
								className="text-sm font-semibold"
								onClick={() => {
									if (!currentUser) {
										setLoginModalOpen(true)
									} else {
										setTrialUpgradeModalOpen(true)
									}
									posthog?.capture('nav-upgrade-button-clicked')
								}}
							>
								<div className="flex items-center gap-2">
									<div>Upgrade now</div>
								</div>
							</Button>
						</div>
					) : (
						!isPremium && (
							<div
								className={`flex gap-2 items-center justify-center md:justify-end w-full md:w-auto ${
									currentUser ? '' : 'md:flex hidden'
								}`}
								onClick={() => {
									if (!currentUser) {
										setLoginModalOpen(true)
									}
									posthog?.capture('nav-upgrade-button-clicked')
								}}
							>
								<div className="bg-gray-200 px-3 rounded-xl flex items-center text-sm py-1">
									Using Limited Free Plan
								</div>
								<Button
									type="default"
									className="text-sm font-semibold"
									onClick={() => {
										if (!currentUser) {
											setLoginModalOpen(true)
										} else {
											setSubscriptionModalOpen(true)
										}
										posthog?.capture('nav-upgrade-button-clicked')
									}}
								>
									<div className="flex items-center gap-2">
										<div>Upgrade</div>
									</div>
								</Button>
							</div>
						)
					)}
				</div>

				<div className=" flex-col gap-2 mt-6 hidden md:flex">
					<div className="text-xl font-bold">Your Productivity Summary</div>
					<div className="grid md:grid-cols-[1fr,1fr] gap-5 mt-2">
						{userPagesLoading ? (
							<>
								<Skeleton variant="rectangular" className="rounded-xl  w-full h-full block" height={130} />
								<Skeleton variant="rectangular" className="rounded-xl w-full h-full block" height={130} />
							</>
						) : (
							<>
								<div className="shadow-sm rounded-xl p-6 border border-l-black border-l-4 bg-white">
									<div className="flex justify-between">
										<div className="text-sm font-semibold">Total Time Saved</div>
										{LuClock({ className: 'shrink-0' })}
									</div>
									<div className="text-2xl font-bold mt-3">{userDocs.length * 3} hours</div>
									{userDocs.length > 0 && (
										<div className="text-xs text-gray-700 font-medium">
											That's almost {Math.ceil(userDocs.length * (3 / 8))} work days!
										</div>
									)}
								</div>
								<div className="shadow-sm rounded-xl p-6 border border-l-black border-l-4 bg-white">
									<div className="flex justify-between">
										<div className="text-sm font-semibold">Documents Created</div>
										{LuFile({ className: 'shrink-0' })}
									</div>
									<div className="text-2xl font-bold mt-3">{userDocs.length}</div>
									<div className="text-xs text-gray-700 font-medium">Avg. 180 mins saved per document</div>
								</div>
							</>
						)}
					</div>
				</div>
				<div className="flex flex-col gap-3 mt-8">
					<div className="flex flex-col gap-3">
						<div className={`text-xl hidden md:block font-bold`}>Tools</div>
						<div className="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4">
							<div className="rounded-xl p-5 flex flex-col gap-4 items-start border shadow-sm bg-secondary generate-card relative">
								<div className="flex flex-col gap-4 items-start grow">
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-2">
											<div className="flex justify-center items-center bg-white rounded-lg h-8 w-8">
												<div className="flex justify-center items-center bg-black rounded-full h-6 w-6">
													{FaPlus({ size: 12, className: 'text-white' })}
												</div>
											</div>
											<div className="text-xl font-semibold">Generate an essay</div>
										</div>
										<div className="text-gray-700 text-sm mt-2">
											Generate an essay, research paper, or any other type of content in seconds, with in-text citations
											and AI bypass.
										</div>
									</div>
									<div className="flex flex-col grow justify-end">
										<ButtonWithArrow type="default" onClick={() => history.push('/new/' + DOC_TEMPLATES.Writer)}>
											Start generating now
										</ButtonWithArrow>
									</div>
								</div>
							</div>
							<div className="rounded-xl p-5 flex flex-col gap-4 items-start border shadow-sm bg-white">
								<div className="flex flex-col gap-4 items-start grow">
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-2">
											<div className="flex justify-center items-center bg-gray-100 rounded-lg h-8 w-8">
												{LuBrain({ size: 18, className: 'text-black' })}
											</div>
											<div className="text-xl font-semibold">Humanize text</div>
										</div>
										<div className="text-gray-700 text-sm mt-2">
											Make AI-generated content undetectable by leading AI detectors and sound human-written.
										</div>
									</div>
									<div className="flex flex-col grow justify-end">
										<ButtonWithArrow type="outline" onClick={() => history.push('/humanizer')}>
											Humanize now
										</ButtonWithArrow>
									</div>
								</div>
							</div>
							<div className="rounded-xl p-5 flex flex-col gap-4 items-start border shadow-sm bg-white">
								<div className="flex flex-col gap-4 items-start grow">
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-2">
											<div className="flex justify-center items-center bg-gray-100 rounded-lg h-8 w-8">
												{LuScanText({ size: 18, className: 'text-black' })}
											</div>
											<div className="text-xl font-semibold">Check for AI</div>
										</div>
										<div className="text-gray-700 text-sm mt-2">
											Check if your text is detected as AI-written using six leading AI detectors.
										</div>
									</div>
									<div className="flex flex-col grow justify-end">
										<ButtonWithArrow type="outline" onClick={() => history.push('/detector')}>
											Detect now
										</ButtonWithArrow>
									</div>
								</div>
							</div>
							<div className="rounded-xl p-5 flex flex-col gap-4 items-start border shadow-sm bg-white">
								<div className="flex flex-col gap-4 items-start grow">
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-2">
											<div className="flex justify-center items-center bg-gray-100 rounded-lg h-8 w-8">
												{LuScanText({ size: 18, className: 'text-black' })}
											</div>
											<div className="text-xl font-semibold">Generate outline</div>
										</div>
										<div className="text-gray-700 text-sm mt-2">
											Generate a detailed outline for your essay, research paper, or any other type of content.
										</div>
									</div>
									<div className="flex flex-col grow justify-end">
										<ButtonWithArrow type="outline" onClick={() => history.push('/new/' + DOC_TEMPLATES.Outline)}>
											Generate now
										</ButtonWithArrow>
									</div>
								</div>
							</div>
						</div>
					</div>
					<UserDocs userDocs={userDocs} setUserDocs={setUserDocs} />
				</div>
			</main>
		</div>
	)
}

export default Doc
